/** Куда ушли деньги на модель.
 *
 *  ПОСТРОЧНО, А НЕ ИТОГОМ. Итог отвечает «потрачено столько» и молчит о том, ЧТО
 *  именно съело бюджет: один разбор месяца стоит как десяток вопросов, и без
 *  разбивки этого не увидеть. Строка расхода пишется на каждый вызов ровно
 *  затем, чтобы этот экран был возможен.
 *
 *  СУММЫ ЗДЕСЬ МЕЛКИЕ, И ЭТО НОРМАЛЬНО. Вопрос стоит доли цента, поэтому мелкие
 *  показываем с четырьмя знаками: округлив всё до копеек, экран превратился бы
 *  в столбец нулей при настоящем расходе.
 */
import { useEffect, useState } from "react";
import { api, type LlmSpend } from "../lib/api.ts";
import { plural } from "../lib/plural.ts";
import { Sheet } from "./Sheet.tsx";

/** Имя вызова человеку ничего не говорит — переводим.
 *
 *  Незнакомое назначение показываем как есть: появится новый вид расхода —
 *  лучше строка «review» в списке, чем молча потерянные деньги. */
const WHAT: Record<string, string> = {
  ask: "Вопросы",
  review: "Разбор месяца",
  stt: "Расшифровка голоса",
  nlu: "Разбор фразы при записи",
  finding: "Находки",
};

/** Чем меряется каждый вид: вопросы штуками, голос записями. */
const UNITS: Record<string, [string, string, string]> = {
  ask: ["вопрос", "вопроса", "вопросов"],
  review: ["раз", "раза", "раз"],
  stt: ["запись", "записи", "записей"],
  nlu: ["фраза", "фразы", "фраз"],
  finding: ["штука", "штуки", "штук"],
};

export function SpendSheet({ onClose }: { onClose: () => void }) {
  const [spend, setSpend] = useState<LlmSpend | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // Тянем при открытии, а не держим в экране: цифра меняется от каждого
    // вопроса, и показать несвежую хуже, чем подождать полсекунды.
    api.llmSpend().then(setSpend).catch(() => setFailed(true));
  }, []);

  return (
    <Sheet title="Расход на разбор" onClose={onClose}>
      {failed && <Note>Не получилось прочитать расход. Попробуйте позже.</Note>}

      {spend && (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span className="num" style={{ fontSize: "var(--f-t1)", fontWeight: 700,
                                           letterSpacing: "-0.02em" }}>
              {money(spend.spent)}
            </span>
            <span className="num" style={{ fontSize: "var(--f-caption)",
                                           color: "var(--text-muted)" }}>
              из {money(spend.budget)} за месяц
            </span>
          </div>

          <div style={{ height: 6, borderRadius: "var(--r-bar)", background: "var(--surface-2)",
                        overflow: "hidden", margin: "var(--s-3) 0 var(--s-4)" }}>
            <div style={{ height: "100%", borderRadius: "var(--r-bar)", width: `${share(spend)}%`,
                          background: spend.low ? "var(--warn)" : "var(--ok)" }} />
          </div>

          {spend.lines.length === 0 ? (
            <Note>
              В этом месяце модель ещё не работала. Счётчик обнуляется первого
              числа сам.
            </Note>
          ) : (
            <div style={{ background: "var(--surface-2)", borderRadius: "var(--r-card)",
                          padding: "2px var(--s-4)", marginBottom: "var(--s-4)" }}>
              {spend.lines.map((line, i) => (
                <div key={line.purpose}
                     style={{ display: "flex", alignItems: "center", gap: "var(--s-3)",
                              padding: "12px 0",
                              borderBottom: i === spend.lines.length - 1
                                ? "none" : "1px solid var(--separator)" }}>
                  <div style={{ flexGrow: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "var(--f-body)" }}>
                      {WHAT[line.purpose] ?? line.purpose}
                    </div>
                    <div className="num" style={{ fontSize: "var(--f-caption-s)",
                                                  color: "var(--text-faint)" }}>
                      {plural(line.calls, UNITS[line.purpose] ?? ["вызов", "вызова", "вызовов"])}
                    </div>
                  </div>
                  <span className="num" style={{ fontSize: "var(--f-body)", fontWeight: 600,
                                                 whiteSpace: "nowrap" }}>
                    {money(line.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}

          <Note>
            {spend.low
              ? "Осталось меньше доллара: ответы станут короче, а на нуле разбор "
                + "замолчит. Записывать траты это не трогает — ввод работает всегда."
              : "Поиск находок в базе бесплатный: их считает запрос, а не модель. "
                + "Записывать траты можно и при пустом бюджете."}
          </Note>
        </>
      )}
    </Sheet>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                lineHeight: 1.5, margin: 0 }}>
      {children}
    </p>
  );
}

/** Мелкие суммы показываем с четырьмя знаками: вопрос стоит около 0,003 $, и
 *  округление до копеек превратило бы список в нули при настоящем расходе. */
function money(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return `${value} $`;
  // Хвостовой ноль в мелкой сумме («0,0080») рядом с двузначными соседями
  // читается как другая точность, а не как та же цифра.
  const shown = n === 0 || n >= 0.01 ? n.toFixed(2) : n.toFixed(4).replace(/0+$/, "");
  return `${shown.replace(".", ",")} $`;
}

/** Единственное место, где деньги становятся числом: здесь нужны пиксели. */
function share(spend: LlmSpend): number {
  const budget = Number(spend.budget);
  if (!Number.isFinite(budget) || budget <= 0) return 0;
  return Math.min(100, Math.max(0, (Number(spend.spent) / budget) * 100));
}
